import React from "react";
import {
  Box,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Typography,
  Chip,
  Paper,
} from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { type AllMattersAnalyzeOptions } from "@/types/acquirebook";

interface Props {
  disabled?: boolean;
  value: AllMattersAnalyzeOptions;
  onChange: (value: AllMattersAnalyzeOptions) => void;
}

export type { AllMattersAnalyzeOptions };

/** 解析方法カードの選択肢定義 */
const ANALYZE_METHODS: Array<{
  type: AllMattersAnalyzeOptions["analyzeMethodType"];
  label: string;
  description: string;
  /** 推奨バッジを表示するか（通常解析へ誘導する目的） */
  recommended?: boolean;
}> = [
  {
    type: "rule_based",
    label: "通常解析",
    description: "速くて正確。まずはこちら。",
    recommended: true,
  },
  {
    type: "llm_api",
    label: "AI解析",
    description: "通常解析で読み取れない登記簿をAIで解析。",
  },
];

/**
 * 解析方法カードの sx を生成する。選択状態の強調・無効状態の減衰を切り替える。
 * レンダーごとに JSX 内でクロージャを再定義しないようモジュールレベルへ切り出す。
 */
const buildAnalyzeCardSx =
  (selected: boolean, disabled: boolean) => (theme: Theme) => ({
    display: "block",
    p: 2,
    borderWidth: 2,
    borderColor: selected ? "primary.main" : "divider",
    bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : "grey.50",
    cursor: disabled ? "default" : "pointer",
    opacity: disabled ? 0.6 : 1,
    transition: "border-color 0.2s, background-color 0.2s",
  });

/**
 * 全部事項解析のオプションを選択するためのコンポーネント
 * @param props - コンポーネントのプロパティ
 * @param props.disabled - コントロールを無効にするか
 * @param props.value - 現在のオプション状態（解析方法・表題部・甲区・乙区）
 * @param props.onChange - オプション変更時のコールバック
 */
export const AllMattersAnalyzeOptionsCheckbox: React.FC<Props> = ({
  disabled = false,
  value,
  onChange,
}) => {
  const handleCheckboxChange =
    (option: "includeKoku" | "includeOtsuku") =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...value, [option]: event.target.checked });
    };

  // 解析方法カード選択時のハンドラ。
  // RadioGroup でラップすることでグループとしてのキーボード矢印移動・
  // スクリーンリーダーの選択肢読み上げ（role="radiogroup"）を担保する。
  const handleMethodTypeChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    methodType: string
  ): void => {
    onChange({
      ...value,
      analyzeMethodType:
        methodType as AllMattersAnalyzeOptions["analyzeMethodType"],
    });
  };

  return (
    <Box sx={{ ml: 4, mt: 1 }}>
      <FormGroup>
        <FormControlLabel
          control={<Checkbox checked={true} disabled={true} />}
          label="表題部（必須）"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={value.includeKoku}
              onChange={handleCheckboxChange("includeKoku")}
              disabled={disabled}
            />
          }
          label="甲区（所有権に関する事項）"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={value.includeOtsuku}
              onChange={handleCheckboxChange("includeOtsuku")}
              disabled={disabled}
            />
          }
          label="乙区（所有権以外の権利に関する事項）"
        />
      </FormGroup>

      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 2, mb: 1 }}>
        解析方法
      </Typography>
      <RadioGroup
        value={value.analyzeMethodType}
        onChange={handleMethodTypeChange}
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        {ANALYZE_METHODS.map((method) => {
          // 選択中のカードを強調し、未選択カードは色味を薄くして通常解析へ誘導する
          const selected = value.analyzeMethodType === method.type;
          return (
            <Paper
              key={method.type}
              component="label"
              variant="outlined"
              sx={buildAnalyzeCardSx(selected, disabled)}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Radio
                  checked={selected}
                  value={method.type}
                  disabled={disabled}
                  size="small"
                  sx={{ p: 0 }}
                />
                <Typography sx={{ fontWeight: "bold" }}>
                  {method.label}
                </Typography>
                {method.recommended && (
                  <Chip
                    label="推奨"
                    size="small"
                    color="primary"
                    sx={{ height: 20, fontSize: "0.7rem" }}
                  />
                )}
              </Box>
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: selected ? "text.primary" : "text.secondary",
                }}
              >
                {method.description}
              </Typography>
            </Paper>
          );
        })}
      </RadioGroup>
    </Box>
  );
};
